import {
  type ConflictKmatDTO,
  type ElementTileDTO,
  type KmatDTO,
  type LinearConfigurationDTO,
  type LinearConfigurationUpdateDTO,
  type LinearCsticDTO,
  type LinearCsticValueDTO,
  type LinearStepDTO,
  type SubstepDTO,
  SubstepType,
  type TileGroupDTO,
} from "./dtos";
import {
  type ConflictKmat,
  type Kmat,
  type LinearConfiguration,
  type LinearConfigurationUpdate,
  type LinearCstic,
  type LinearCsticValue,
  type LinearStep,
  type LinearSubstep,
  type LinearSubstepUpdate,
  type SubstepTile,
  type TileGroup,
} from "./models";
import {
  CSTIC_DESIGN_TYPE_NAME,
  CSTIC_SUPPORT_KIT_NAME,
  CSTIC_VALUE_WILDCARD_IS_SET,
  CSTIC_VHOE,
  CSTIC_VSKITS_NAME,
  DISCLAIMER_RAIL_ABOVE,
  DISCLAIMER_RAIL_BELOW,
  FAKE_CSTIC_CONFIGURATION_VERSION,
  FAKE_CSTIC_UNIT_LENGTH,
  GET_ORDER_DESIGNTION_TEXT,
  GREASING_KEY_DEFAULT_KMAT,
  GREASING_OPTION_KEY_DEFAULT_KMAT,
  GREASING_OPTION_STANDARD_VALUE,
  GREASING_STANDARD_VALUE,
  ORDER_DESIGNATION_HTML_IDS,
  SCHMIERANSCHLUSS_TAB_ONE,
  SCHMIERANSCHLUSS_TAB_TWO,
} from "./constants";
import { groupByFn, isNonNull, toMapFn } from "../../utils/reducers";
import { ConfiguratorType } from "../ConfiguratorType";
import { CARRIAGE_DETAILS_INFO_MSG } from "../Carriage/carriageConfiguratorConstants";
import { userIsPublic, userIsRegistered } from "../../utils/roles";
import {
  resolvePotentiallyExternalLabel,
  type TransformationContext,
  transformToExternalUrl,
} from "../common/translationUtils";
import { buildAdvancedTranslationsForUnit, RESET_STANDARD_AND_LENGTH } from "./advancedTranslationsTransformer";
import { getConfiguratorDefinition } from "~/Configurators/configuratorRegistry";
import { getKmatType, KmatType } from "~/Configurators/Linear/getKmatType";
import {
  chamfer,
  radialThread,
  SHAFT_LENGTH_INCH_MAX,
  SHAFT_LENGTH_INCH_MIN,
  SHAFT_LENGTH_METRIC_MAX,
  SHAFT_LENGTH_METRIC_MIN,
} from "~/Configurators/Shaft/inch/inchCsticConstants";
import { REFERENCE_MATERIAL_CSTIC } from "~/Configurators/constants";
import type { TranslationModel } from "~/common/TranslationModel";
import { pushStandardValueIfMissing } from "~/Configurators/common/v2/helper";
import {
  RESET_SELECTION,
  RESET_SELECTION_AND_LENGTH,
  RESET_STANDARD,
  resolveInternalAdvancedTranslation,
} from "~/Configurators/common/advancedTranslationUtils";
import { applyConfigurationFromDefinition } from "~/Configurators/common/v2/applyConfigurationFromDefinition";
import { addFakeCstic } from "~/Configurators/transformerHelper";
import { getMainKmat } from "~/Configurators/Shaft/common/commonShaftHelpers";

const getType = <T extends { type: string }>(i: T) => i.type;

export function transform(dto: LinearConfigurationDTO, configuratorType: ConfiguratorType): LinearConfiguration {
  const context: TransformationContext = {
    productCode: dto.productCode,
    configuratorType,
    baseExternalUrl: dto.baseExternalUrl,
    specialTranslations: dto.specialTranslations ?? {},
  };
  const kmats = dto.kmats.filter((kmat) => kmat.cstics.length > 0).map((kmat) => transformKmat(kmat, context));

  if (configuratorType === ConfiguratorType.System) {
    addFakeCstic(kmats, FAKE_CSTIC_CONFIGURATION_VERSION, dto.configVersion ?? "");

    const length = dto.length === 0 ? null : dto.length?.toString() ?? "";
    addFakeCstic(kmats, FAKE_CSTIC_UNIT_LENGTH, length);
  }

  const kmatMap = groupByFn(kmats, (kmat) => kmat.type);
  addMandatoryValuesForLubricationOptions(kmatMap, configuratorType);
  const steps = dto.steps ?? [];
  const substeps = toMapFn(
    steps.flatMap((step) => (step.substeps || []).map((substep) => transformSubstep(substep, context, kmatMap))),
    getType,
  );
  const guidewayKmats = kmatMap[KmatType.GUIDEWAY];
  const guidewayPieceLimits = transformGuidewayPieceLimits(dto, guidewayKmats);
  let configuration: LinearConfiguration = {
    ...dto,
    kmats: kmatMap,
    steps: steps.map((step, index) => transformStep(step, index, context)),
    substeps,
    schaefflerId: dto.schaefflerId ?? "",
    guidewayPieceLimits,
    allowedSteps: dto.allowedSteps,
    isConfigComplete: dto.complete ?? false,
    currentStep: dto.currentStep ?? 1,
    highestVisitedStep: dto.highestVisitedStep ?? 1,
    designation: dto.designation ?? "-",
    description: "",
    mediaUrl: "",
    specialTranslations: buildTranslationsMap(context),
    advancedSpecialTranslations: buildAdvancedTranslationsMap(context, dto),
    enableCarriageEdit: dto.enableCarriageEdit,
  };

  if (context.configuratorType === ConfiguratorType.InchShaft || configuratorType === ConfiguratorType.MetricShaft) {
    configuration.description = dto.description ?? "";
    configuration.mediaUrl = dto.mediaUrl;
  } else {
    configuration.description = resolveSpecialTranslation(
      "SUMMARY_IMAGE_AND_DESCRIPTION",
      "description",
      resolvePotentiallyExternalLabel(dto.description ?? "", context),
      context,
    );
    configuration.mediaUrl = resolveProductImage(dto.mediaUrl, substeps.SystemLayout, context);
  }

  const configuratorDefinition = getConfiguratorDefinition(configuration.productCode);
  if (configuratorDefinition.modelPreprocessor) {
    configuration = configuratorDefinition.modelPreprocessor(configuration);
  }
  if (configuratorDefinition.modelAndUpdatePreprocessor) {
    configuration = configuratorDefinition.modelAndUpdatePreprocessor(configuration, dto);
  }

  const mainKmat = getMainKmat({ kmats: configuration.kmats });
  const subKmats = kmats.slice(1);
  applyConfigurationFromDefinition(configuration, configuratorType, mainKmat, subKmats);

  if (configuratorDefinition.postDefinitionProcessor) {
    configuration = configuratorDefinition.postDefinitionProcessor(configuration, dto);
  }

  return configuration;
}

export function transformUpdate(
  dto: LinearConfigurationUpdateDTO,
  configuratorType: ConfiguratorType,
  csticsToPreserve?: LinearCstic[],
): LinearConfigurationUpdate {
  const context: TransformationContext = {
    productCode: dto.productCode,
    configuratorType,
    baseExternalUrl: dto.baseExternalUrl,
    specialTranslations: dto.specialTranslations ?? {},
  };

  const kmats = dto.kmats.filter((kmat) => kmat.cstics.length > 0).map((kmat) => transformKmat(kmat, context));

  if (configuratorType === ConfiguratorType.System) {
    addFakeCstic(kmats, FAKE_CSTIC_CONFIGURATION_VERSION, dto.configVersion ?? "");

    const length = dto.length === 0 ? null : dto.length?.toString() ?? "";
    addFakeCstic(kmats, FAKE_CSTIC_UNIT_LENGTH, length);
  }

  const kmatMap = groupByFn(kmats, (kmat) => kmat.type);
  const mainKmat = getMainKmat({ kmats: kmatMap });

  csticsToPreserve?.forEach((cstic) => (mainKmat.cstics[cstic.name] = cstic));

  addMandatoryValuesForLubricationOptions(kmatMap, configuratorType);
  const steps = dto.steps ?? [];
  const substeps = toMapFn(
    steps.flatMap((step) => (step.substeps || []).map((substep) => transformSubstep(substep, context, kmatMap))),
    getType,
  );

  const guidewayKmats = kmatMap[KmatType.GUIDEWAY];

  const guidewayPieceLimits = transformGuidewayPieceLimits(dto, guidewayKmats);

  let update: LinearConfigurationUpdate = {
    ...dto,
    kmats: kmatMap,
    steps: steps.map((step, index) => transformStep(step, index, context)),
    substeps,
    schaefflerId: dto.schaefflerId ?? "",
    guidewayPieceLimits,
    conflictedCstics: (dto.conflictedCstics || []).map(transformConflictKmat),
    designation: dto.designation ?? "-",
    isConfigComplete: dto.complete ?? false,
    currentStep: dto.currentStep ?? 1,
    highestVisitedStep: dto.highestVisitedStep ?? 1,
    description: "",
    mediaUrl: "",
    specialTranslations: buildTranslationsMap(context),
    advancedSpecialTranslations: buildAdvancedTranslationsMap(context, dto),
    enableCarriageEdit: dto.enableCarriageEdit,
  };

  if (context.configuratorType === ConfiguratorType.InchShaft || configuratorType === ConfiguratorType.MetricShaft) {
    update.description = dto.description ?? "";
    update.mediaUrl = dto.mediaUrl;
  } else {
    update.description = resolveSpecialTranslation(
      "SUMMARY_IMAGE_AND_DESCRIPTION",
      "description",
      resolvePotentiallyExternalLabel(dto.description ?? "", context),
      context,
    );
    update.mediaUrl = resolveProductImage(dto.mediaUrl, substeps.SystemLayout, context);
  }

  const configuratorDefinition = getConfiguratorDefinition(context.productCode);
  if (configuratorDefinition.modelAndUpdatePreprocessor) {
    update = configuratorDefinition.modelAndUpdatePreprocessor(update, dto);
  }

  const subKmats = kmats.slice(1);
  applyConfigurationFromDefinition(update, configuratorType, kmats[0], subKmats);

  if (configuratorDefinition.postDefinitionProcessor) {
    update = configuratorDefinition.postDefinitionProcessor(update, dto);
  }

  return update;
}

export function transformStep(dto: LinearStepDTO, stepIndex: number, context: TransformationContext): LinearStep {
  const stepData = getConfiguratorDefinition(context.productCode).stepHardcodedData[stepIndex];
  return {
    ...dto,
    substeps: dto.substeps ? dto.substeps.map((element) => element.type) : [],
    defaultValues: getDefaultValues(dto.substeps),
    resetLabel: stepData.resetLabelKey,
    showResetConfirmation: stepData.resetRequiresConfirmation,
  };
}

function getDefaultValues(substeps: SubstepDTO[] | null) {
  let defaultValues: Dict<string> = {};
  if (substeps) {
    substeps.forEach((substep) => {
      if (substep.defaultValues) {
        defaultValues = { ...defaultValues, ...substep.defaultValues };
      }
    });
  }
  return defaultValues;
}

export function transformSubstep(
  dto: SubstepDTO,
  context: TransformationContext,
  kmatMap: Dict<Kmat[]>,
): LinearSubstep {
  return {
    ...dto,
    tiles: dto.tiles ? dto.tiles.map((item) => transformTile(item, dto, context, kmatMap)) : [],
    groupedTiles: dto.groupedTiles ? dto.groupedTiles.map((group) => transformTileGroup(group, context, kmatMap)) : [],
    tilesPerRow: dto.tilesPerRow || 0,
    selectedTile: null,
    value: null,
    additionalValidations: {},
    additionalData: {},
    isVisible: dto.visible,
    footerInfo:
      dto.footerInfo && Array.isArray(dto.footerInfo)
        ? dto.footerInfo.map((item) => resolvePotentiallyExternalLabel(item, context))
        : [dto.footerInfo].filter(isNonNull).map((item) => resolvePotentiallyExternalLabel(item, context)),
    name: resolvePotentiallyExternalLabel(dto.name, context),
    description: resolvePotentiallyExternalLabel(dto.description, context),
    info: resolvePotentiallyExternalLabel(dto.info, context),
    extraTexts: {},
    inputCstics: {},
    helperCstics: {},
  };
}

function transformSubstepUpdate(
  dto: SubstepDTO,
  context: TransformationContext,
  kmatMap: Dict<Kmat[]>,
): LinearSubstepUpdate {
  const model: LinearSubstepUpdate = transformSubstep(dto, context, kmatMap);

  delete model.value;
  delete model.selectedTile;

  // if (!dto.dynamicSubstep) {
  //   delete model.tiles;
  //   delete model.groupedTiles;
  //   delete model.tilesPerRow;
  // }

  return model;
}

export function transformTileGroup(
  dto: TileGroupDTO,
  context: TransformationContext,
  kmatMap: Dict<Kmat[]>,
): TileGroup {
  let name;
  if (context.configuratorType !== ConfiguratorType.System) {
    name = transformToExternalUrl(dto.name, context.baseExternalUrl);
  } else {
    name = dto.name;
  }
  return {
    ...dto,
    name,
    tiles: dto.tiles ? dto.tiles.map((item) => transformTile(item, null, context, kmatMap)) : [],
  };
}

export function transformTile(
  dto: ElementTileDTO,
  substepDTO: SubstepDTO | null,
  context: TransformationContext,
  kmatMap: Dict<Kmat[]>,
): SubstepTile {
  const csticsList = Object.entries(dto.cstics)
    .filter(([name, val]) => "value" in val || val.clear)
    .map(([name, val]) => ("value" in val ? ([name, val.value] as const) : ([name, null] as const)));
  const cstics = toMapFn(
    csticsList,
    ([name, _]) => name,
    ([_, val]) => val,
  );
  let csticsToEnable: Dict<string | number> = toMapFn(
    csticsList.filter(isNonNull),
    ([name, _]) => name,
    ([_, val]) => val,
  ) as Dict<string | number>;

  if (context.configuratorType === ConfiguratorType.System) {
    // TODO: Can this come from BE?
    if (substepDTO) {
      if (substepDTO.type === SubstepType.DesignType) {
        // Special case to not disable tile 4 by accident in step 1 substep 2
        delete csticsToEnable[CSTIC_VHOE];
      } else if (substepDTO.type === SubstepType.Size) {
        // Special case to disable all tiles when design type is NOT selected
        csticsToEnable[CSTIC_DESIGN_TYPE_NAME] = CSTIC_VALUE_WILDCARD_IS_SET;
      } else if (substepDTO.type === SubstepType.Lubrication) {
        csticsToEnable = {};
      } else if (substepDTO.type === SubstepType.ReferenceCarriage) {
        delete csticsToEnable[CSTIC_VSKITS_NAME];
      }
    }
  }

  let name;
  if (dto.nameIsExternalTranslation) {
    name = resolvePotentiallyExternalLabel(dto.name, context);
  } else {
    name = dto.name;
  }

  const description = resolvePotentiallyExternalLabel(dto.description, context);
  const info = resolvePotentiallyExternalLabel(dto.info, context);

  return {
    ...dto,
    csticsToEnable,
    cstics,
    name,
    description,
    info,
    isDisabledExternally: dto.disabled ?? false,
  };
}

function transformKmat(dto: KmatDTO, context: TransformationContext): Kmat {
  const cstic = dto.cstics[0];

  return {
    ...dto,
    cstics: toMapFn(
      dto.cstics.map((cstic) => transformCstic(cstic, context, dto.parentId)),
      (cstic) => cstic.key,
    ),
    type: getKmatType(cstic.key),
  };
}

export function transformUpdateStep(dto: LinearStepDTO): Partial<LinearStep> {
  const dynamicSubsteps = dto.substeps || [];
  return {
    ...dto,
    substeps: [],
    defaultValues: getDefaultValues(dynamicSubsteps),
  };
}

function transformCstic(dto: LinearCsticDTO, context: TransformationContext, parentGroupId?: string): LinearCstic {
  let label = null;
  let name = null;
  let info = null;

  const idParts = dto.key.split("@");
  const key = idParts.pop()!;
  const groupId = idParts.join("@");

  if (context.configuratorType !== ConfiguratorType.System) {
    // The "KIT" label in configure carriage comes as a special translation for carriage configurator & others,
    // but inside CSTIC_SUPPORT_KIT_NAME for regular linear configurator
    if (dto.name === CSTIC_SUPPORT_KIT_NAME) {
      label = resolveSpecialTranslation("KIT", "name", dto.label, context);
    }
  }

  // Default way of filling out label, name, info
  if (label === null) {
    label = dto.label;
  }
  if (name === null) {
    name = resolveSpecialTranslation(dto.name, "name", dto.name, context);
  }
  if (info === null) {
    info = resolveSpecialTranslation(dto.name, "info", dto.info, context);
  }

  const cstic: LinearCstic = {
    ...dto,
    key,
    groupId,
    parentGroupId,
    values: dto.values.map((value) => transformCsticValue(dto, value, context)),
    description: resolveSpecialTranslation(dto.name, "description", dto.description, context),
    name,
    info,
    label,
    readonly: READ_ONLY_CSTICS.has(key),
    fake: false,
  };
  return cstic;
}

function transformCsticValue(
  csticDTO: LinearCsticDTO,
  valueDTO: LinearCsticValueDTO,
  context: TransformationContext,
): LinearCsticValue {
  const val: LinearCsticValue = {
    ...valueDTO,
    standard: valueDTO.standard ?? valueDTO.defaultValue,
    label: resolveSpecialTranslation(csticDTO.name + "_" + valueDTO.value, "name", valueDTO.label, context),
  };

  return val;
}

function transformConflictKmat(dto: ConflictKmatDTO): ConflictKmat {
  return {
    ...dto,
    cstics: dto.cstics ? dto.cstics : [],
  };
}

function transformGuidewayPieceLimits(
  dto: LinearConfigurationUpdateDTO | LinearConfigurationDTO,
  guidewayKmats: Kmat[],
) {
  return Object.entries(dto.guidewayPieceLimits || {})
    .map(([subInstanceId, { minLength, maxLength }]) => {
      const index = guidewayKmats.findIndex((kmat) => kmat.id.split("-")[0] === subInstanceId);
      return [index, { min: minLength, max: maxLength }] as const;
    })
    .reduce(
      (map, [key, val]) => {
        map[key] = val;
        return map;
      },
      {} as Dict<{ min: number; max: number }>,
    );
}

function resolveSpecialTranslation(
  key: string,
  fieldName: "name" | "info" | "description" | "image",
  defaultValue: string,
  context: TransformationContext,
) {
  const val = context.specialTranslations[key]?.[fieldName];
  if (val) {
    return resolvePotentiallyExternalLabel(val, context);
  }
  return defaultValue;
}
function resolveProductImage(
  mediaUrl: string,
  systemLayout: LinearSubstep | LinearSubstepUpdate | undefined,
  context: TransformationContext,
) {
  if (context.configuratorType === ConfiguratorType.System && systemLayout) {
    return systemLayout?.tiles?.[systemLayout.selectedTile!]?.imageUrl ?? "";
  } else {
    return resolveSpecialTranslation("SUMMARY_IMAGE_AND_DESCRIPTION", "image", mediaUrl ?? "", context);
  }
}

function buildTranslationsMap(context: TransformationContext) {
  const translations = {};

  if (context.configuratorType === ConfiguratorType.System) {
    translations[SCHMIERANSCHLUSS_TAB_ONE] = "page.lc.schmieranchluss.tab1";
    translations[SCHMIERANSCHLUSS_TAB_TWO] = "page.lc.schmieranchluss.tab2";
  }

  if (context.specialTranslations["VWAUBW_065"]) {
    translations[CARRIAGE_DETAILS_INFO_MSG] = transformToExternalUrl(
      context.specialTranslations["VWAUBW_065"].additionalTexts[0],
      context.baseExternalUrl,
    );
  }

  const isMediasUser = userIsRegistered() || userIsPublic();
  if (context.configuratorType === ConfiguratorType.System) {
    translations[DISCLAIMER_RAIL_BELOW] = GET_ORDER_DESIGNTION_TEXT(ORDER_DESIGNATION_HTML_IDS[isMediasUser ? 2 : 0]);
    translations[DISCLAIMER_RAIL_ABOVE] = GET_ORDER_DESIGNTION_TEXT(ORDER_DESIGNATION_HTML_IDS[isMediasUser ? 3 : 1]);
  }

  return translations;
}

function buildAdvancedTranslationsMap(
  context: TransformationContext,
  dto: LinearConfigurationDTO | LinearConfigurationUpdateDTO,
): Dict<TranslationModel> {
  let translations: Record<string, TranslationModel> = {};
  if (context.configuratorType === ConfiguratorType.System) {
    translations = buildAdvancedTranslationsForUnit(context, dto);
  }

  resolveInternalAdvancedTranslation(translations, RESET_SELECTION, "page.linear.resetStep.1");
  resolveInternalAdvancedTranslation(translations, RESET_STANDARD_AND_LENGTH, "page.linear.resetStep.2");
  resolveInternalAdvancedTranslation(translations, RESET_STANDARD, "page.linear.resetStep.3");
  resolveInternalAdvancedTranslation(translations, RESET_SELECTION_AND_LENGTH, "page.linear.resetStep.4");

  return translations;
}

function addMandatoryValuesForLubricationOptions(kmatMap: Dict<Kmat[]>, configuratorType: ConfiguratorType) {
  if (configuratorType === ConfiguratorType.System) {
    const kmat = kmatMap[KmatType.DEFAULT][0];
    pushStandardValueIfMissing(kmat, GREASING_KEY_DEFAULT_KMAT, GREASING_STANDARD_VALUE);
    pushStandardValueIfMissing(kmat, GREASING_OPTION_KEY_DEFAULT_KMAT, GREASING_OPTION_STANDARD_VALUE);
  }
}

const READ_ONLY_CSTICS = new Set([
  REFERENCE_MATERIAL_CSTIC,
  SHAFT_LENGTH_METRIC_MIN,
  SHAFT_LENGTH_INCH_MIN,
  SHAFT_LENGTH_INCH_MAX,
  SHAFT_LENGTH_METRIC_MAX,
  radialThread.aL.inch.min,
  radialThread.aL.inch.max,
  radialThread.aL.metric.min,
  radialThread.aL.metric.max,
  radialThread.aR.inch.min,
  radialThread.aR.inch.max,
  radialThread.aR.metric.min,
  radialThread.aR.metric.max,
  radialThread.pdt,
  radialThread.threadSpacing.inch,
  radialThread.threadSpacing.metric,
  radialThread.threadHoleAmount,
  radialThread.threadDepth,
  chamfer.left.width.nf,
  chamfer.left.width.minmax,
  chamfer.right.width.nf,
  chamfer.right.width.minmax,
]);
